import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import { useRoute } from 'wouter';
import {
  CHANGE_DRIVER_ACTION,
  ESCAPE_KEY,
  PARKING_COMMENT_ACTION,
  PARKING_PURPOSE_ACTION,
  RIDE_COMMENT_ACTION,
  RIDE_PURPOSE_ACTION,
  SHOW_RIDE_ACTION,
  OVERVIEW_KEY,
  TYPE_PRIVATE_ACTION,
  TYPE_PUBLIC_ACTION,
} from '../constants';
import {
  ICheckedTableItems,
  IFilterTripsOverview,
  ITripsOverviewAdvancedFilterForRequest,
  ITripsOverviewTableData,
} from '../interfaces';
import LoaderService from '../../../Services/LoaderService';
import useOutsideClick from '../../../hooks/useOutsideClick';
import useKeyPress from '../../../hooks/useKeyPress';
import showMessage from '../../Toastr/ToastService';
import { selectedVehiclesAtom } from '../../../store/recoil/vehicles';
import {
  advancedFiltersAtom,
  checkedTableItemsAtom,
  filterForRequestAtom,
  isActiveAdvanceFilterAtom,
  rideBookDataAtom,
} from '../TripsOverviewState';
import { convertTimeToSeconds, getMultipleActions, handleFilterKey } from '../TripsOverviewService';
import useAnalytics from '../../../hooks/useAnalytics';

interface IBulkOperationsProps {
  handleAction: (action: string) => void;
}

const BULK_OPERATIONS_LIMIT = 20000;

function BulkOperations(props: IBulkOperationsProps): JSX.Element | null {
  const tableData = useRecoilValue<ITripsOverviewTableData | null>(rideBookDataAtom);
  const { formatMessage: f } = useIntl();
  const [countRows, setCountRows] = React.useState<number>(0);
  const [actionsMenu, setActionsMenu] = React.useState<string[]>([]);
  const [isMenuOpened, setIsMenuOpened] = React.useState(false);
  const [checkboxConfigChanged, setCheckboxConfigChanged] = React.useState(false);
  const checkedTableItems = useRecoilValue<ICheckedTableItems>(checkedTableItemsAtom);
  const filterForRequest = useRecoilValue<IFilterTripsOverview>(filterForRequestAtom);
  const selectedVehicles = useRecoilValue<number[]>(selectedVehiclesAtom);
  const [, paramsVehicleId] = useRoute('/overview-tours/:vehicleId');
  const isActiveAdvanceFilter = useRecoilValue(isActiveAdvanceFilterAtom);
  const advancedFilterValues = useRecoilValue(advancedFiltersAtom);
  const { trackEvent } = useAnalytics();

  const clickOutsideRef: React.RefObject<HTMLDivElement> = React.createRef();

  useOutsideClick(clickOutsideRef, () => {
    setIsMenuOpened(false);
  });
  useKeyPress(ESCAPE_KEY, () => {
    setIsMenuOpened(false);
  });

  const handleCount = React.useCallback(() => {
    let count = 0;
    if (checkedTableItems.mainCheckbox && tableData) {
      count = tableData.availableCount;
    } else {
      Object.keys(checkedTableItems.rideIds).forEach((customerId: string) => {
        count += checkedTableItems.rideIds[customerId].length;
      });
    }
    Object.keys(checkedTableItems.rideIdBlacklist).forEach((customerId: string) => {
      count -= checkedTableItems.rideIdBlacklist[customerId].length;
    });
    if (tableData) {
      checkedTableItems.intervals.forEach((interval: { min: string; max: string }) => {
        tableData.summaries
          .filter((i) => i.type !== OVERVIEW_KEY)
          .forEach((item) => {
            if (item.from === interval.min && item.to === interval.max) {
              count += item.rideCount || 0;
            }
          });
      });
      checkedTableItems.intervalsBlacklist.forEach((interval: { min: string; max: string }) => {
        tableData.summaries
          .filter((i) => i.type !== OVERVIEW_KEY)
          .forEach((item) => {
            if (item.from === interval.min && item.to === interval.max) {
              count -= item.rideCount || 0;
            }
          });
      });
    }
    setCountRows(count);
  }, [
    checkedTableItems.intervals,
    checkedTableItems.intervalsBlacklist,
    checkedTableItems.mainCheckbox,
    checkedTableItems.rideIdBlacklist,
    checkedTableItems.rideIds,
    tableData,
  ]);

  React.useEffect(() => {
    handleCount();
  }, [handleCount]);

  React.useEffect(() => {
    setIsMenuOpened(false);
    setCheckboxConfigChanged(true);
  }, [checkedTableItems]);

  const handleAction = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    const actionId = event.currentTarget.getAttribute('data-id');
    if (actionId) {
      trackEvent(['ride book', 'click', `Ride book bulk action ${actionId}`]);
      props.handleAction(actionId);
    }
  };

  const handleActionMenu = async (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    trackEvent(['ride book', 'click', 'Ride book bulk action menu']);
    let actions: string[] | false = [];
    if (!isMenuOpened) {
      if (checkboxConfigChanged) {
        LoaderService.showLoader(true);
        const advancedFilters: ITripsOverviewAdvancedFilterForRequest = handleFilterKey(
          {
            ...advancedFilterValues,
            timeFrom: convertTimeToSeconds(advancedFilterValues.timeFrom),
            timeTo: convertTimeToSeconds(advancedFilterValues.timeTo),
          },
          isActiveAdvanceFilter
        );
        const filter = {
          vehicle:
            paramsVehicleId && paramsVehicleId.vehicleId
              ? [Number(paramsVehicleId.vehicleId)]
              : selectedVehicles,
          ...advancedFilters,
          ...filterForRequest,
        };
        actions = await getMultipleActions(checkedTableItems, filter);
        setActionsMenu(actions || []);
        setCheckboxConfigChanged(false);
        LoaderService.showLoader(false);
        if (actions && actions.length > 0) {
          setIsMenuOpened(true);
        } else if (actions) {
          showMessage(f({ id: 'toastr.info' }), f({ id: 'bulk.noActions' }), 'info', null);
        }
      } else if (actionsMenu.length > 0) {
        setIsMenuOpened(true);
      } else {
        showMessage(f({ id: 'toastr.info' }), f({ id: 'bulk.noActions' }), 'info', null);
      }
    } else {
      setIsMenuOpened(false);
    }
  };

  return countRows > 0 ? (
    <div className="b-seletion-bar">
      <div className="row no-gutters">
        <div className="col-12 col-sm-auto mr-2 fs-1 mb-1 mb-sm-0">
          {' '}
          {`${f({ id: 'table.rideBook.chosen' })}:`}{' '}
          <span className="d-sm-block font-weight-bold">
            {countRows}/{tableData?.availableCount}
          </span>
        </div>
        <div className="col-12 col-sm-auto">
          <a
            onClick={handleAction}
            data-id="action_show"
            className="e-button mr-2 px-2"
            href="#"
            data-cy="multiple-action_show"
          >
            <i className="cmd-icon cmd-icon-colapse-row-action mr-2 fs-4" />
            {f({ id: 'rides.showDetails' })}
          </a>
        </div>
        <div className="col-12 col-sm-auto">
          <a
            onClick={handleAction}
            data-id="action_hide"
            className="e-button mr-2 px-2"
            href="#"
            data-cy="multiple-action_hide"
          >
            <i className="cmd-icon cmd-icon-expand-row-action mr-2 fs-4" />
            {f({ id: 'rides.hideDetails' })}
          </a>
        </div>
        {countRows <= BULK_OPERATIONS_LIMIT && (
          <div ref={clickOutsideRef} className="col-12 col-sm-auto">
            <a
              onClick={handleActionMenu}
              id="action_all"
              className="e-button px-2"
              href="#"
              data-cy="multiple-action_show"
            >
              {/* <i className="cmd-icons cmd-icon-more-horizontal mr-xs fs-4" /> */}
              {f({ id: 'bulk.all' })}
              <i
                className={`cmd-icons cmd-icon-${isMenuOpened ? 'dropup' : 'dropdown'} ml-2 fs-4`}
              />
            </a>
            {isMenuOpened && (
              <div
                className="ridebook-dropdown-menu dropdown-menu dropdown-menu-right dropdown-menu-custom fs-2 mt-1"
                style={{ display: 'block', width: '200px' }}
                aria-labelledby="dropdown-actions-1"
              >
                <div>
                  <div
                    className="px-3 py-2 font-weight-bold border-bottom-on"
                    style={{ color: '#333' }}
                  >
                    {' '}
                    {f({ id: 'bulk.rides' })}
                  </div>
                  {actionsMenu.includes('assignRidePurpose') && (
                    <a
                      data-cy="ride-action-addUsage"
                      href="#"
                      title={f({ id: 'tripsOverview.modal.toAddUsage' })}
                      className="px-3 py-2 d-block text-decoration-none text-gray"
                      onClick={handleAction}
                      data-id={RIDE_PURPOSE_ACTION}
                    >
                      <i
                        className="cmd-icon cmd-icon-single-folded-content-add mr-2 fs-4"
                        style={{ marginLeft: '2px' }}
                      />

                      {f({ id: 'tripsOverview.modal.toAddUsage' })}
                    </a>
                  )}
                  {actionsMenu.includes('changeRideComment') && (
                    <a
                      data-cy="ride-action-addComment"
                      href="#"
                      title={f({ id: 'tripsOverview.modal.toAddComment' })}
                      className="px-3 py-2 d-block text-decoration-none text-gray"
                      onClick={handleAction}
                      data-id={RIDE_COMMENT_ACTION}
                    >
                      <i className="cmd-icon cmd-icon-comment-add mr-2 fs-4" />
                      {f({ id: 'tripsOverview.modal.toAddComment' })}
                    </a>
                  )}
                  {actionsMenu.includes('changeDriver') && (
                    <a
                      data-cy="change-driver"
                      href="#"
                      title={f({ id: 'tripsOverview.modal.toChangeDriver' })}
                      className="px-3 py-2 d-block text-decoration-none text-gray"
                      onClick={handleAction}
                      data-id={CHANGE_DRIVER_ACTION}
                    >
                      <i
                        className="cmd-icon cmd-icon-single-user mr-2 fs-4"
                        style={{ marginLeft: '2px' }}
                      />
                      {f({ id: 'tripsOverview.modal.toChangeDriver' })}
                    </a>
                  )}
                  {actionsMenu.includes('changePrivacy') && (
                    <a
                      data-cy="ride-action-modal-publicRide"
                      href="#"
                      title={f({
                        id: 'rides.publicRides',
                      })}
                      className="px-3 py-2 d-block text-decoration-none text-gray"
                      onClick={handleAction}
                      data-id={TYPE_PUBLIC_ACTION}
                    >
                      {' '}
                      <i className="cmd-icon cmd-icon-ride-mode-work mr-2 fs-4" />
                      {f({ id: 'rides.publicRides' })}
                    </a>
                  )}
                  {actionsMenu.includes('changePrivacy') && (
                    <a
                      data-cy="ride-action-modal-privateRide"
                      href="#"
                      title={f({
                        id: 'rides.privateRides',
                      })}
                      className="px-3 py-2 d-block text-decoration-none text-gray"
                      onClick={handleAction}
                      data-id={TYPE_PRIVATE_ACTION}
                    >
                      {' '}
                      <i className="cmd-icon cmd-icon-shifting-mode-private mr-2 fs-4" />
                      {f({ id: 'rides.privateRides' })}
                    </a>
                  )}
                  {actionsMenu.includes('showRides') && (
                    <a
                      data-cy="ride-action-rideMap"
                      href="/trip-map"
                      onClick={handleAction}
                      data-id={SHOW_RIDE_ACTION}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={f({ id: 'rides.showRides' })}
                      className="px-3 py-2 d-block text-decoration-none text-gray"
                    >
                      <i className="cmd-icon cmd-icon-play-simple mr-2 fs-4" />
                      {f({ id: 'rides.showRides' })}
                    </a>
                  )}
                </div>

                <div>
                  <div
                    className="px-3 py-2 font-weight-bold border-bottom-on"
                    style={{ color: '#333' }}
                  >
                    {f({ id: 'bulk.parkings' })}
                  </div>
                  {actionsMenu.includes('assignParkingPurpose') && (
                    <a
                      href="#"
                      title={f({ id: 'tripsOverview.modal.toAddUsage' })}
                      data-cy="parking-action-addUsage"
                      className="px-3 py-2 d-block text-decoration-none text-gray"
                      onClick={handleAction}
                      data-id={PARKING_PURPOSE_ACTION}
                    >
                      <i
                        className="cmd-icon cmd-icon-single-folded-content-add mr-2 fs-4"
                        style={{ marginLeft: '2px' }}
                      />
                      {f({ id: 'tripsOverview.modal.toAddUsage' })}
                    </a>
                  )}
                  {actionsMenu.includes('changeParkingComment') && (
                    <a
                      href="#"
                      title={f({ id: 'tripsOverview.modal.toAddComment' })}
                      data-cy="parking-action-addComment"
                      className="px-3 py-2 d-block text-decoration-none text-gray"
                      onClick={handleAction}
                      data-id={PARKING_COMMENT_ACTION}
                    >
                      <i className="cmd-icon cmd-icon-comment-add mr-2 fs-4" />
                      {f({ id: 'tripsOverview.modal.toAddComment' })}
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  ) : null;
}

export default BulkOperations;
